import type { SnackBarConfig } from '@shared/components/Snackbar';
import { Box, CircularProgress, Divider, Typography } from '@mui/material';
import axios from 'axios';
import { motion } from 'framer-motion';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import LineChart from '@shared/components/Charts/charts/LineChart';
import Snackbar from '@shared/components/Snackbar';
import { UserContext } from '@/shared/contexts/UserContext';
import { log } from '../../utils/log';

type ReviewDepth = 'BASIC' | 'DEEP_DIVE';

interface ReviewDataPoint {
  reviewCompleted: boolean;
  reviewDepth: ReviewDepth;
  createdAt: string;
  companyName: string;
}

interface ReviewStatsApiResponse {
  data: ReviewDataPoint[];
  totalBasic: number;
  totalDeepDive: number;
  totalAll: number;
  topCompaniesThisWeek: Array<{ companyName: string; count: number }>;
}

const PageWrapper = styled.div`
  padding: 24px;
  max-width: 1000px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const StatBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 32px;
  justify-content: space-between;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const StatCard = styled(motion.div)`
  background: #ffffff;
  padding: 20px;
  border-radius: 12px;
  flex: 1;
  min-width: 120px;
  text-align: center;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.06);
`;

const TopCompaniesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CompanyCard = styled(motion.div)`
  background: #f7f9fc;
  padding: 16px 20px;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
`;

function AdminReviewStats() {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [reviewStats, setReviewStats] = useState<ReviewStatsApiResponse | null>(null);
  const [snackbarConfig, setSnackbarConfig] = useState<SnackBarConfig | null>(null);

  const groupedByDate = new Map<string, number>();
  const tooltipMeta: Record<
    string,
    Array<{
      companyName: string;
      reviewDepth: string;
      completed: boolean;
    }>
  > = {};

  reviewStats?.data.forEach((entry) => {
    const day = entry.createdAt.slice(0, 10);
    groupedByDate.set(day, (groupedByDate.get(day) || 0) + 1);
    tooltipMeta[day] = tooltipMeta[day] || [];
    tooltipMeta[day].push({
      companyName: entry.companyName,
      reviewDepth: entry.reviewDepth,
      completed: entry.reviewCompleted,
    });
  });

  const chartLabels = Array.from(groupedByDate.keys()).sort();
  const chartData = chartLabels.map((label) => groupedByDate.get(label) || 0);

  const getStats = async () => {
    if (!user?.idToken) return;
    setLoading(true);

    try {
      const res = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/admin/company/stats`, {
        headers: {
          Authorization: user?.idToken,
        },
      });
      setReviewStats(res.data);
    } catch (error) {
      log.error('Error fetching review stats:', error);
      setSnackbarConfig({
        message: 'An error has occurred, please try again later',
        type: 'error',
        open: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getStats();
  }, [user?.idToken]);

  return (
    <PageWrapper>
      <Typography variant="h5" gutterBottom>
        Review Statistics
      </Typography>

      {loading && (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      )}

      {!loading && reviewStats && (
        <>
          <StatBox>
            <StatCard
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0 }}
            >
              <Typography variant="subtitle1">Total Reviews</Typography>
              <Typography variant="h6">{reviewStats.totalAll}</Typography>
            </StatCard>
            <StatCard
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <Typography variant="subtitle1">Basic Deal Retros</Typography>
              <Typography variant="h6">{reviewStats.totalBasic}</Typography>
            </StatCard>
            <StatCard
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <Typography variant="subtitle1">Deal Retros</Typography>
              <Typography variant="h6">{reviewStats.totalDeepDive}</Typography>
            </StatCard>
          </StatBox>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Deal Retros Over Time
          </Typography>
          <LineChart
            labels={chartLabels}
            data={chartData}
            labelText="Deal Retro Activity"
            animateChart
            tooltipMeta={tooltipMeta}
          />

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Top Companies This Week
          </Typography>

          <TopCompaniesWrapper>
            {reviewStats.topCompaniesThisWeek.map((c, i) => (
              <CompanyCard
                key={c.companyName}
                whileHover={{ scale: 1.02 }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                <Typography
                  variant="body1"
                  fontWeight={600}
                  onClick={() => navigate(`/company/${encodeURIComponent(c.companyName!)}`)}
                >
                  {i + 1}.{c.companyName}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {c.count} reviews
                </Typography>
              </CompanyCard>
            ))}
          </TopCompaniesWrapper>
        </>
      )}

      {snackbarConfig && <Snackbar config={snackbarConfig} setOpen={() => setSnackbarConfig(null)} />}
    </PageWrapper>
  );
}

export default AdminReviewStats;
